'use client'

import { useState } from 'react'
import { Bell, MapPin, GraduationCap, ChevronDown, ChevronUp, Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react'

interface Institution {
  institution: {
    id: string
    name: string
    type: string
  }
  graduationYear?: number | null
}

interface SubscribeSimilarProps {
  memorial: {
    id: string
    city: string | null
    postalCode: string | null
    institutions?: Institution[]
  }
}

type SubscribeOption = 'city' | 'institution' | 'memorial'

export function SubscribeSimilar({ memorial }: SubscribeSimilarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [selectedOption, setSelectedOption] = useState<SubscribeOption | null>(null)
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const institutions = memorial.institutions || []
  const hasCity = !!memorial.city
  const hasInstitutions = institutions.length > 0

  // Don't render if there's nothing to subscribe to
  if (!hasCity && !hasInstitutions) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedOption) {
      setError('Bitte wählen Sie eine Option aus')
      return
    }

    setError('')
    setLoading(true)

    try {
      const body: Record<string, unknown> = { email }

      if (selectedOption === 'city') {
        body.type = 'REGION'
        body.city = memorial.city
        body.postalCode = memorial.postalCode
      } else if (selectedOption === 'institution' && selectedInstitutionId) {
        body.type = 'INSTITUTION'
        body.institutionId = selectedInstitutionId
      } else if (selectedOption === 'memorial') {
        body.type = 'MEMORIAL'
        body.memorialId = memorial.id
      }

      const res = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Ein Fehler ist aufgetreten')
        return
      }

      setSuccess(true)
    } catch (err) {
      setError('Ein Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  const handleOptionSelect = (option: SubscribeOption, institutionId?: string) => {
    setSelectedOption(option)
    setSelectedInstitutionId(institutionId || null)
    setError('')
  }

  if (success) {
    return (
      <section className="py-8 bg-white border-t border-memorial-border">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-green-50 rounded-xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-green-800 mb-1">Bestätigen Sie Ihre E-Mail</h3>
              <p className="text-green-700 text-sm">
                Wir haben Ihnen eine E-Mail an <strong>{email}</strong> gesendet.
                Bitte klicken Sie auf den Link, um Ihre Benachrichtigungen zu aktivieren.
              </p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-8 bg-white border-t border-memorial-border">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-4 bg-memorial-muted rounded-xl hover:bg-memorial-muted/80 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-memorial-primary" />
            <span className="font-medium text-memorial-text-primary">
              Über ähnliche Sterbefälle informiert werden
            </span>
          </div>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-memorial-text-secondary" />
          ) : (
            <ChevronDown className="w-5 h-5 text-memorial-text-secondary" />
          )}
        </button>

        {isOpen && (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Options */}
            <div className="space-y-2">
              <p className="text-sm text-memorial-text-secondary mb-3">
                Wählen Sie, worüber Sie informiert werden möchten:
              </p>

              {/* City option */}
              {hasCity && (
                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedOption === 'city'
                      ? 'border-memorial-primary bg-memorial-primary/5'
                      : 'border-memorial-border hover:border-memorial-primary/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="subscribeOption"
                    checked={selectedOption === 'city'}
                    onChange={() => handleOptionSelect('city')}
                    className="mt-1 accent-memorial-primary"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-memorial-primary" />
                      <span className="font-medium text-memorial-text-primary">
                        Sterbefälle in {memorial.city}
                      </span>
                    </div>
                    <p className="text-sm text-memorial-text-secondary mt-1">
                      Täglich über neue Sterbefälle in dieser Stadt informiert werden
                    </p>
                  </div>
                </label>
              )}

              {/* Institution options */}
              {institutions.map((ic) => (
                <label
                  key={ic.institution.id}
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedOption === 'institution' && selectedInstitutionId === ic.institution.id
                      ? 'border-memorial-primary bg-memorial-primary/5'
                      : 'border-memorial-border hover:border-memorial-primary/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="subscribeOption"
                    checked={selectedOption === 'institution' && selectedInstitutionId === ic.institution.id}
                    onChange={() => handleOptionSelect('institution', ic.institution.id)}
                    className="mt-1 accent-memorial-primary"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-memorial-primary" />
                      <span className="font-medium text-memorial-text-primary">
                        {ic.institution.name}
                      </span>
                    </div>
                    <p className="text-sm text-memorial-text-secondary mt-1">
                      Über ehemalige Mitglieder dieser Institution informiert werden
                    </p>
                  </div>
                </label>
              ))}
            </div>

            {/* Email input */}
            {selectedOption && (
              <div>
                <label htmlFor="subscribe-email" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                  Ihre E-Mail-Adresse
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary" />
                  <input
                    type="email"
                    id="subscribe-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 text-red-700 rounded-xl p-3 flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {/* Submit */}
            {selectedOption && (
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3 px-6 rounded-xl bg-memorial-primary text-white font-medium hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Wird gespeichert...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Benachrichtigungen aktivieren</span>
                  </>
                )}
              </button>
            )}

            <p className="text-xs text-memorial-text-secondary text-center">
              Sie erhalten täglich eine Zusammenfassung, wenn neue Sterbefälle veröffentlicht werden.
              Abmeldung jederzeit möglich.
            </p>
          </form>
        )}
      </div>
    </section>
  )
}
